import type { GetInstructorsUnapprovedResult } from '@features/instructors/api/get-instructors-unapproved/getInstructorsUnapproved.types';
import type { InstructorSection } from '@features/instructors/contexts/manage-instructors-selection/ManageInstructorsSelectionContext.types';

import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { useOrganizations } from '@contexts/organizations-provider';
import { usePostInstructorsRequestsBatch } from '@features/instructors/hooks/use-post-instructors-requests-batch/usePostInstructorsRequestsBatch';
import { FLAG_MESSAGE_DURATION } from '@shared/constants/flagMessage.constants';
import * as React from 'react';

import type {
  InstructorsProcessingRequestsContextValue,
  InstructorsProcessingRequestsProviderProps,
} from './InstructorsProcessingRequestsContext.types';

import { buildApproveInstructorsRequest } from './InstructorsProcessingRequestsProvider.utils';

const InstructorsProcessingRequestsContext = React.createContext<
  InstructorsProcessingRequestsContextValue | undefined
>(undefined);

const InstructorsProcessingRequestsProvider = React.memo(
  ({
    children,
  }: Readonly<InstructorsProcessingRequestsProviderProps>): React.ReactElement => {
    const [processingCount, setProcessingCount] = React.useState<number>(0);
    const { selectedOrganization } = useOrganizations();
    const customerId = selectedOrganization?.customerId ?? '';
    const { mutate } = usePostInstructorsRequestsBatch({ customerId });

    const isProcessing = processingCount > 0;

    const submitRequests = React.useCallback(
      (
        selectedIds: Record<InstructorSection, Set<string>>,
        data: GetInstructorsUnapprovedResult | undefined,
        onSuccess?: () => void
      ): void => {
        const payload = buildApproveInstructorsRequest(selectedIds, data);
        const submittedRequestCount =
          payload.groundSchool.length +
          payload.simulator.length +
          payload.simCheck.length;
        if (submittedRequestCount === 0 || !customerId) {
          return;
        }

        setProcessingCount(submittedRequestCount);

        mutate(payload, {
          onSuccess: response => {
            setProcessingCount(0);
            onSuccess?.();
            if (response.failureCount === 0) {
              emitFlagMessage(
                'success',
                `${response.successCount} approval ${response.successCount === 1 ? 'request' : 'requests'} successfully submitted!`,
                FLAG_MESSAGE_DURATION
              );
            } else {
              emitFlagMessage(
                'danger',
                `${response.totalRequests} approval ${response.totalRequests === 1 ? 'request' : 'requests'} submitted, ${response.failureCount} failed.`,
                FLAG_MESSAGE_DURATION
              );
            }
          },
          onError: () => {
            setProcessingCount(0);
            emitFlagMessage(
              'danger',
              'Failed to submit approval requests. Please try again.',
              FLAG_MESSAGE_DURATION
            );
          },
        });
      },
      [mutate, customerId]
    );

    const contextValue: InstructorsProcessingRequestsContextValue =
      React.useMemo(
        () => ({ isProcessing, processingCount, submitRequests }),
        [isProcessing, processingCount, submitRequests]
      );

    return (
      <InstructorsProcessingRequestsContext value={contextValue}>
        {children}
      </InstructorsProcessingRequestsContext>
    );
  }
);

InstructorsProcessingRequestsProvider.displayName =
  'InstructorsProcessingRequestsProvider';

export {
  InstructorsProcessingRequestsContext,
  InstructorsProcessingRequestsProvider,
};
